export interface TechnologyDetail {
  slug: string;
  name: string;
  tagline: string;
  hero: {
    badge: string;
    headline: string;
    subtext: string;
    primaryCTA: string;
    secondaryCTA: string;
  };
  overview: { title: string; text: string; image: string };
  whyWeUse: { title: string; desc: string; icon: string }[];
  capabilities: { title: string; desc: string; icon: string }[];
  stack: { name: string; logo?: string; icon?: string }[];
  architecture: { name: string; desc: string; icon: string }[];
  benefits: { title: string; desc: string; icon: string }[];
  relatedServices: { title: string; desc: string; icon: string; href: string }[];
  faq: { q: string; a: string }[];
}

export const technologyDetails: TechnologyDetail[] = [
  {
    slug: "laravel",
    name: "Laravel",
    tagline: "Laravel Development Expertise",
    hero: {
      badge: "Primary Expertise",
      headline: "Laravel Development Expertise",
      subtext:
        "Build secure, scalable and high-performance web applications with experienced Laravel developers.",
      primaryCTA: "Hire Laravel Developer",
      secondaryCTA: "Start Your Project",
    },
    overview: {
      title: "Laravel is our core backend engineering capability",
      text: "We use Laravel to build secure APIs, SaaS platforms, enterprise applications and high-performance digital products. From clean architecture to Eloquent ORM, Queues, Caching and cloud deployment, our Laravel expertise delivers maintainable systems built to scale.",
      image: "https://readdy.ai/api/search-image?query=Modern%20Laravel%20development%20concept%2C%20clean%20code%20editor%20with%20PHP%20backend%20framework%2C%20abstract%20tech%20visualization%2C%20red%20and%20navy%20gradient%20lighting%2C%20premium%20software%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-laravel-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "Clean Architecture", desc: "Expressive syntax and MVC structure keep codebases organized.", icon: "ri-organization-chart" },
      { title: "Security", desc: "Built-in protection against the most common web vulnerabilities.", icon: "ri-shield-check-line" },
      { title: "Rapid Development", desc: "Powerful tools and generators accelerate delivery.", icon: "ri-rocket-line" },
      { title: "Scalability", desc: "Queues, caching and database optimization support growth.", icon: "ri-expand-diagonal-line" },
      { title: "API Development", desc: "First-class REST API support for web and mobile backends.", icon: "ri-exchange-line" },
      { title: "Cloud Ready", desc: "Deploys cleanly to AWS, DigitalOcean and more.", icon: "ri-cloud-line" },
    ],
    capabilities: [
      { title: "Laravel Architecture", desc: "Clean, maintainable Laravel application architecture.", icon: "ri-organization-chart" },
      { title: "Laravel APIs", desc: "Secure and scalable REST APIs built with Laravel.", icon: "ri-exchange-line" },
      { title: "Laravel SaaS", desc: "Multi-tenant SaaS platforms with billing and roles.", icon: "ri-cloud-line" },
      { title: "Enterprise Laravel", desc: "Robust applications for large organizations.", icon: "ri-building-4-line" },
      { title: "Laravel Performance", desc: "Query optimization, caching and queue tuning.", icon: "ri-speed-line" },
      { title: "Laravel Security", desc: "Hardening, auth and secure deployment practices.", icon: "ri-shield-check-line" },
      { title: "Laravel Migration", desc: "Move legacy PHP applications to modern Laravel.", icon: "ri-movie-line" },
      { title: "Laravel Maintenance", desc: "Ongoing support, updates and improvements.", icon: "ri-tools-line" },
    ],
    stack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg" },
    ],
    architecture: [
      { name: "Frontend", desc: "Vue.js / React UI", icon: "ri-window-line" },
      { name: "Laravel", desc: "Application & business logic", icon: "ri-code-s-slash-line" },
      { name: "REST API", desc: "JSON API layer", icon: "ri-exchange-line" },
      { name: "Database", desc: "MySQL / PostgreSQL", icon: "ri-database-2-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "Cloud Infrastructure", desc: "AWS / Docker", icon: "ri-cloud-line" },
    ],
    benefits: [
      { title: "Faster Delivery", desc: "Rapid development with proven tooling.", icon: "ri-rocket-line" },
      { title: "Lower Costs", desc: "Efficient code reduces long-term maintenance.", icon: "ri-money-cny-circle-line" },
      { title: "Secure by Default", desc: "Security best practices built in.", icon: "ri-shield-check-line" },
      { title: "Scalable", desc: "Grows with your business.", icon: "ri-expand-diagonal-line" },
    ],
    relatedServices: [
      { title: "Laravel Development", desc: "Build production-ready Laravel applications.", icon: "ri-code-s-slash-line", href: "/services/laravel-development" },
      { title: "Hire Laravel Developer", desc: "Dedicated Laravel developers for your team.", icon: "ri-user-line", href: "/hire-laravel-developer" },
      { title: "API Development", desc: "Secure Laravel REST APIs.", icon: "ri-exchange-line", href: "/services/api-development" },
      { title: "Cloud & DevOps", desc: "Deploy Laravel on AWS.", icon: "ri-server-line", href: "/services/cloud-devops" },
    ],
    faq: [
      { q: "Why choose Laravel for a new project?", a: "Laravel offers clean syntax, powerful tooling, strong security and an excellent ecosystem — ideal for building scalable APIs and SaaS platforms quickly and maintainably." },
      { q: "Can you migrate an existing PHP application to Laravel?", a: "Yes. We modernize legacy PHP codebases to Laravel while preserving business logic and data." },
      { q: "Can you build SaaS using Laravel?", a: "Absolutely. We build multi-tenant SaaS platforms with subscriptions, billing and role-based access." },
      { q: "Can you optimize an existing Laravel application?", a: "Yes. We improve query performance, caching, queues and overall application speed." },
    ],
  },
  {
    slug: "php",
    name: "PHP",
    tagline: "Custom PHP Development",
    hero: {
      badge: "Backend Technology",
      headline: "Custom PHP Development",
      subtext:
        "Secure, high-performance PHP applications and APIs built on modern, maintainable architecture.",
      primaryCTA: "Start Your Project",
      secondaryCTA: "Talk to a Laravel Expert",
    },
    overview: {
      title: "Modern PHP beyond the legacy reputation",
      text: "We build custom PHP applications, APIs and integrations with modern practices — clean architecture, secure coding, performance optimization and database excellence. We also modernize legacy PHP systems into maintainable, scalable platforms.",
      image: "https://readdy.ai/api/search-image?query=Modern%20PHP%20development%20concept%2C%20abstract%20server-side%20code%20visualization%2C%20purple%20and%20navy%20gradient%20lighting%2C%20premium%20software%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-php-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "Custom PHP", desc: "Tailored applications beyond framework constraints.", icon: "ri-code-box-line" },
      { title: "Laravel", desc: "Modern PHP framework for most builds.", icon: "ri-code-s-slash-line" },
      { title: "API Development", desc: "Robust REST API backends.", icon: "ri-exchange-line" },
      { title: "Legacy Modernization", desc: "Upgrade old PHP systems safely.", icon: "ri-refresh-line" },
      { title: "Performance", desc: "Optimized execution and caching.", icon: "ri-speed-line" },
      { title: "Database Integration", desc: "Clean data layers and migrations.", icon: "ri-database-2-line" },
    ],
    capabilities: [
      { title: "Custom PHP Applications", desc: "Business logic built to your exact needs.", icon: "ri-code-box-line" },
      { title: "PHP APIs", desc: "Secure, versioned backend APIs.", icon: "ri-exchange-line" },
      { title: "Laravel on PHP", desc: "Modern PHP through the Laravel framework.", icon: "ri-code-s-slash-line" },
      { title: "Legacy Modernization", desc: "Safely modernize aging PHP codebases.", icon: "ri-refresh-line" },
      { title: "Performance Tuning", desc: "Faster requests and lower resource use.", icon: "ri-speed-line" },
      { title: "Security Hardening", desc: "Protect against common PHP vulnerabilities.", icon: "ri-shield-check-line" },
    ],
    stack: [
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
    ],
    architecture: [
      { name: "HTTP / Frontend", desc: "Web & mobile clients", icon: "ri-window-line" },
      { name: "PHP Application", desc: "Laravel / custom PHP", icon: "ri-code-box-line" },
      { name: "REST API", desc: "Data exchange layer", icon: "ri-exchange-line" },
      { name: "Database", desc: "MySQL / PostgreSQL", icon: "ri-database-2-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "Server", desc: "Nginx / Docker / AWS", icon: "ri-server-line" },
    ],
    benefits: [
      { title: "Cost Effective", desc: "Efficient PHP solutions at scale.", icon: "ri-money-cny-circle-line" },
      { title: "Widely Supported", desc: "Runs anywhere PHP is available.", icon: "ri-global-line" },
      { title: "Secure", desc: "Modern security practices.", icon: "ri-shield-check-line" },
      { title: "Maintainable", desc: "Clean, structured code.", icon: "ri-tools-line" },
    ],
    relatedServices: [
      { title: "Web Development", desc: "Custom PHP web applications.", icon: "ri-window-line", href: "/services/web-development" },
      { title: "Laravel Development", desc: "Modern PHP with Laravel.", icon: "ri-code-s-slash-line", href: "/services/laravel-development" },
      { title: "API Development", desc: "PHP backend APIs.", icon: "ri-exchange-line", href: "/services/api-development" },
    ],
    faq: [
      { q: "Is PHP still a good choice?", a: "Yes. Modern PHP with frameworks like Laravel is fast, secure and powers a huge share of the web." },
      { q: "Can you modernize my legacy PHP application?", a: "Yes. We refactor legacy code into modern, maintainable PHP or migrate to Laravel." },
    ],
  },
  {
    slug: "flutter",
    name: "Flutter",
    tagline: "Cross-Platform Mobile Apps",
    hero: {
      badge: "Mobile Technology",
      headline: "Cross-Platform Mobile Apps with Flutter",
      subtext:
        "Beautiful, high-performance iOS and Android apps from a single Dart codebase, powered by robust Laravel APIs.",
      primaryCTA: "Build Your Mobile App",
      secondaryCTA: "Start Your Project",
    },
    overview: {
      title: "One codebase, every platform",
      text: "We build cross-platform mobile applications with Flutter that deliver native-level performance on both iOS and Android. Combined with robust Laravel APIs, our mobile apps handle authentication, payments, push notifications and real-time features seamlessly.",
      image: "https://readdy.ai/api/search-image?query=Modern%20Flutter%20mobile%20app%20development%20concept%2C%20smartphone%20with%20abstract%20UI%20screens%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20premium%20mobile%20app%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-flutter-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "iOS & Android", desc: "Native performance from one codebase.", icon: "ri-smartphone-line" },
      { title: "UI/UX Excellence", desc: "Pixel-perfect custom interfaces.", icon: "ri-palette-line" },
      { title: "Fast Development", desc: "Hot reload speeds iteration.", icon: "ri-rocket-line" },
      { title: "Laravel API", desc: "Robust backend integration.", icon: "ri-exchange-line" },
      { title: "Push Notifications", desc: "Engagement that works.", icon: "ri-notification-3-line" },
      { title: "Payments", desc: "In-app and gateway payments.", icon: "ri-bank-card-line" },
    ],
    capabilities: [
      { title: "Flutter Development", desc: "Cross-platform apps from one codebase.", icon: "ri-code-s-slash-line" },
      { title: "iOS Apps", desc: "Polished iOS experiences.", icon: "ri-apple-line" },
      { title: "Android Apps", desc: "High-performance Android apps.", icon: "ri-android-line" },
      { title: "UI/UX Design", desc: "Custom, pixel-perfect interfaces.", icon: "ri-palette-line" },
      { title: "Push Notifications", desc: "Real-time user engagement.", icon: "ri-notification-3-line" },
      { title: "Laravel API Integration", desc: "Secure mobile backends.", icon: "ri-exchange-line" },
    ],
    stack: [
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
      { name: "Dart", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dart/dart-original.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
      { name: "Firebase", icon: "ri-fire-line" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
    ],
    architecture: [
      { name: "Flutter App", desc: "iOS & Android UI", icon: "ri-smartphone-line" },
      { name: "REST API", desc: "JSON data layer", icon: "ri-exchange-line" },
      { name: "Laravel Backend", desc: "Business logic", icon: "ri-code-s-slash-line" },
      { name: "Database", desc: "MySQL / PostgreSQL", icon: "ri-database-2-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "Cloud", desc: "AWS / push notifications", icon: "ri-cloud-line" },
    ],
    benefits: [
      { title: "Lower Cost", desc: "One codebase for two platforms.", icon: "ri-money-cny-circle-line" },
      { title: "Faster Launch", desc: "Ship to both stores sooner.", icon: "ri-rocket-line" },
      { title: "Consistent UX", desc: "Identical experience everywhere.", icon: "ri-user-smile-line" },
      { title: "Scalable", desc: "Grows with your user base.", icon: "ri-expand-diagonal-line" },
    ],
    relatedServices: [
      { title: "Mobile App Development", desc: "Flutter mobile apps.", icon: "ri-smartphone-line", href: "/services/mobile-app-development" },
      { title: "API Development", desc: "Mobile-friendly Laravel APIs.", icon: "ri-exchange-line", href: "/services/api-development" },
      { title: "Laravel Development", desc: "Powerful mobile backends.", icon: "ri-code-s-slash-line", href: "/services/laravel-development" },
    ],
    faq: [
      { q: "Can Flutter really match native performance?", a: "Yes. Flutter compiles to native code and delivers smooth 60fps performance across platforms." },
      { q: "Do you integrate mobile apps with Laravel APIs?", a: "Yes. We build secure REST APIs in Laravel to power mobile apps." },
    ],
  },
  {
    slug: "vue-js",
    name: "Vue.js",
    tagline: "Modern Frontend Applications",
    hero: {
      badge: "Frontend Technology",
      headline: "Modern Frontend Applications with Vue.js",
      subtext:
        "Fast, reactive and maintainable user interfaces for dashboards, SaaS products and web applications.",
      primaryCTA: "Start Your Project",
      secondaryCTA: "Talk to a Laravel Expert",
    },
    overview: {
      title: "Reactive, performant frontends",
      text: "We build modern single-page applications with Vue.js — dashboards, SaaS interfaces and responsive UIs — wired to powerful Laravel APIs. Our Vue applications are fast, accessible and easy to maintain.",
      image: "https://readdy.ai/api/search-image?query=Modern%20Vue.js%20frontend%20development%20concept%2C%20abstract%20web%20dashboard%20UI%20visualization%2C%20green%20and%20navy%20gradient%20lighting%2C%20premium%20frontend%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-vue-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "Dashboards", desc: "Rich, real-time admin interfaces.", icon: "ri-dashboard-line" },
      { title: "SaaS Interfaces", desc: "Scalable product UIs.", icon: "ri-cloud-line" },
      { title: "SPA", desc: "Fast single-page applications.", icon: "ri-window-line" },
      { title: "API Integration", desc: "Seamless Laravel API wiring.", icon: "ri-exchange-line" },
      { title: "Responsive UI", desc: "Works on every screen.", icon: "ri-smartphone-line" },
      { title: "Performance", desc: "Optimized bundle and rendering.", icon: "ri-speed-line" },
    ],
    capabilities: [
      { title: "SPA Development", desc: "Fast, reactive single-page apps.", icon: "ri-window-line" },
      { title: "Dashboards", desc: "Data-dense admin interfaces.", icon: "ri-dashboard-line" },
      { title: "SaaS Interfaces", desc: "Scalable product UIs.", icon: "ri-cloud-line" },
      { title: "Responsive UI", desc: "Mobile-first layouts.", icon: "ri-smartphone-line" },
      { title: "API Integration", desc: "Robust Laravel API wiring.", icon: "ri-exchange-line" },
      { title: "Performance", desc: "Fast load and render.", icon: "ri-speed-line" },
    ],
    stack: [
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
    ],
    architecture: [
      { name: "Vue.js Frontend", desc: "SPA & dashboards", icon: "ri-window-line" },
      { name: "REST API", desc: "JSON data layer", icon: "ri-exchange-line" },
      { name: "Laravel Backend", desc: "Business logic & APIs", icon: "ri-code-s-slash-line" },
      { name: "Database", desc: "MySQL / PostgreSQL", icon: "ri-database-2-line" },
      { name: "Redis", desc: "Cache & sessions", icon: "ri-stack-line" },
      { name: "Cloud", desc: "AWS / CDN", icon: "ri-cloud-line" },
    ],
    benefits: [
      { title: "Fast UI", desc: "Reactive and responsive.", icon: "ri-speed-line" },
      { title: "Maintainable", desc: "Component-based code.", icon: "ri-tools-line" },
      { title: "Scalable", desc: "Grows with features.", icon: "ri-expand-diagonal-line" },
      { title: "Great UX", desc: "Modern, accessible interfaces.", icon: "ri-user-smile-line" },
    ],
    relatedServices: [
      { title: "Web Development", desc: "Vue.js web applications.", icon: "ri-window-line", href: "/services/web-development" },
      { title: "SaaS Development", desc: "Vue.js SaaS interfaces.", icon: "ri-cloud-line", href: "/services/saas-development" },
      { title: "API Development", desc: "Laravel APIs for Vue apps.", icon: "ri-exchange-line", href: "/services/api-development" },
    ],
    faq: [
      { q: "Is Vue.js good for dashboards?", a: "Yes. Vue's reactivity and component model make it excellent for data-dense dashboards." },
      { q: "Can Vue apps connect to Laravel APIs?", a: "Absolutely. We build Vue SPAs that consume Laravel REST APIs." },
    ],
  },
  {
    slug: "react",
    name: "React",
    tagline: "Scalable Web Applications",
    hero: {
      badge: "Frontend Technology",
      headline: "Scalable Web Applications with React",
      subtext:
        "Flexible, component-driven frontends for web applications, dashboards and SaaS products.",
      primaryCTA: "Start Your Project",
      secondaryCTA: "Talk to a Laravel Expert",
    },
    overview: {
      title: "Component-driven, developer-friendly",
      text: "We build scalable web applications with React using a clean component architecture, wired to powerful Laravel APIs. From interactive dashboards to full SaaS products, our React frontends are fast, accessible and maintainable.",
      image: "https://readdy.ai/api/search-image?query=Modern%20React%20development%20concept%2C%20abstract%20component-based%20UI%20visualization%2C%20blue%20and%20cyan%20gradient%20lighting%2C%20premium%20frontend%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-react-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "Web Applications", desc: "Rich, interactive web apps.", icon: "ri-window-line" },
      { title: "Dashboards", desc: "Data-dense admin UIs.", icon: "ri-dashboard-line" },
      { title: "SaaS Products", desc: "Scalable product frontends.", icon: "ri-cloud-line" },
      { title: "Component Architecture", desc: "Reusable, testable components.", icon: "ri-box-3-line" },
      { title: "API Integration", desc: "Seamless Laravel wiring.", icon: "ri-exchange-line" },
      { title: "Ecosystem", desc: "Mature tooling and libraries.", icon: "ri-tools-line" },
    ],
    capabilities: [
      { title: "React Web Apps", desc: "Modern single-page applications.", icon: "ri-window-line" },
      { title: "Dashboards", desc: "Real-time admin interfaces.", icon: "ri-dashboard-line" },
      { title: "SaaS Frontends", desc: "Scalable product UIs.", icon: "ri-cloud-line" },
      { title: "Component Library", desc: "Reusable design systems.", icon: "ri-box-3-line" },
      { title: "API Integration", desc: "Laravel backend wiring.", icon: "ri-exchange-line" },
      { title: "Performance", desc: "Fast load and render.", icon: "ri-speed-line" },
    ],
    stack: [
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
    ],
    architecture: [
      { name: "React Frontend", desc: "SPA & dashboards", icon: "ri-window-line" },
      { name: "REST API", desc: "JSON data layer", icon: "ri-exchange-line" },
      { name: "Laravel Backend", desc: "Business logic & APIs", icon: "ri-code-s-slash-line" },
      { name: "Database", desc: "MySQL / PostgreSQL", icon: "ri-database-2-line" },
      { name: "Redis", desc: "Cache & sessions", icon: "ri-stack-line" },
      { name: "Cloud", desc: "AWS / CDN", icon: "ri-cloud-line" },
    ],
    benefits: [
      { title: "Flexible", desc: "Mature ecosystem.", icon: "ri-settings-3-line" },
      { title: "Fast", desc: "Optimized rendering.", icon: "ri-speed-line" },
      { title: "Reusable", desc: "Component architecture.", icon: "ri-box-3-line" },
      { title: "Scalable", desc: "Grows with features.", icon: "ri-expand-diagonal-line" },
    ],
    relatedServices: [
      { title: "Web Development", desc: "React web applications.", icon: "ri-window-line", href: "/services/web-development" },
      { title: "SaaS Development", desc: "React SaaS products.", icon: "ri-cloud-line", href: "/services/saas-development" },
      { title: "API Development", desc: "Laravel APIs for React.", icon: "ri-exchange-line", href: "/services/api-development" },
    ],
    faq: [
      { q: "Is React good for dashboards and SaaS?", a: "Yes. React's component model and ecosystem make it ideal for dashboards and SaaS products." },
      { q: "Do you use React with Laravel backends?", a: "Yes. We build React frontends that consume Laravel REST APIs." },
    ],
  },
  {
    slug: "node-js",
    name: "Node.js",
    tagline: "Real-Time & API Services",
    hero: {
      badge: "Backend Technology",
      headline: "Real-Time & API Services with Node.js",
      subtext:
        "Fast, event-driven backends for APIs, real-time applications, microservices and integrations.",
      primaryCTA: "Start Your Project",
      secondaryCTA: "Talk to a Laravel Expert",
    },
    overview: {
      title: "Event-driven, real-time power",
      text: "We use Node.js to build fast APIs, real-time applications, microservices and system integrations. Its event-driven, non-blocking model makes it ideal for chat, live dashboards, streaming and high-concurrency services.",
      image: "https://readdy.ai/api/search-image?query=Modern%20Node.js%20development%20concept%2C%20abstract%20server-side%20event-driven%20visualization%2C%20green%20and%20navy%20gradient%20lighting%2C%20premium%20backend%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-node-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "APIs", desc: "Fast, scalable REST APIs.", icon: "ri-exchange-line" },
      { title: "Real-time", desc: "WebSockets & live data.", icon: "ri-radio-line" },
      { title: "Microservices", desc: "Modular backend services.", icon: "ri-box-3-line" },
      { title: "Integrations", desc: "Third-party system wiring.", icon: "ri-links-line" },
      { title: "Backend Systems", desc: "Robust server-side logic.", icon: "ri-server-line" },
      { title: "High Concurrency", desc: "Handles many connections.", icon: "ri-expand-diagonal-line" },
    ],
    capabilities: [
      { title: "Node.js APIs", desc: "Fast REST & GraphQL services.", icon: "ri-exchange-line" },
      { title: "Real-time Apps", desc: "WebSocket-driven experiences.", icon: "ri-radio-line" },
      { title: "Microservices", desc: "Independent, scalable services.", icon: "ri-box-3-line" },
      { title: "Integrations", desc: "Connect third-party systems.", icon: "ri-links-line" },
      { title: "Backend Systems", desc: "Robust server-side logic.", icon: "ri-server-line" },
      { title: "Performance", desc: "High-concurrency handling.", icon: "ri-speed-line" },
    ],
    stack: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg" },
      { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    ],
    architecture: [
      { name: "Clients", desc: "Web & mobile", icon: "ri-window-line" },
      { name: "Node.js Service", desc: "APIs & real-time", icon: "ri-code-box-line" },
      { name: "REST / WebSocket", desc: "Data exchange", icon: "ri-radio-line" },
      { name: "Database", desc: "PostgreSQL / Redis", icon: "ri-database-2-line" },
      { name: "Message Queue", desc: "Async processing", icon: "ri-stack-line" },
      { name: "Cloud", desc: "AWS / Docker", icon: "ri-cloud-line" },
    ],
    benefits: [
      { title: "Fast", desc: "Non-blocking performance.", icon: "ri-speed-line" },
      { title: "Real-time", desc: "Live data streaming.", icon: "ri-radio-line" },
      { title: "Scalable", desc: "Handles high concurrency.", icon: "ri-expand-diagonal-line" },
      { title: "Flexible", desc: "Works alongside Laravel.", icon: "ri-settings-3-line" },
    ],
    relatedServices: [
      { title: "API Development", desc: "Node.js APIs & microservices.", icon: "ri-exchange-line", href: "/services/api-development" },
      { title: "Cloud & DevOps", desc: "Deploy Node services.", icon: "ri-server-line", href: "/services/cloud-devops" },
      { title: "SaaS Development", desc: "Node.js in SaaS platforms.", icon: "ri-cloud-line", href: "/services/saas-development" },
    ],
    faq: [
      { q: "When should I use Node.js?", a: "Node.js is ideal for real-time apps, high-concurrency APIs, microservices and integrations." },
      { q: "Can Node.js work alongside Laravel?", a: "Yes. Laravel can power core business logic while Node handles real-time services." },
    ],
  },
  {
    slug: "aws",
    name: "AWS",
    tagline: "Scalable Cloud Infrastructure",
    hero: {
      badge: "Cloud Technology",
      headline: "Scalable Cloud Infrastructure on AWS",
      subtext:
        "Design, deploy and manage reliable AWS infrastructure — EC2, RDS, S3, security, monitoring and CI/CD.",
      primaryCTA: "Optimize Your Infrastructure",
      secondaryCTA: "Start Your Project",
    },
    overview: {
      title: "Reliable, secure cloud foundations",
      text: "We design and manage AWS infrastructure for performance, security and scalability. From EC2 and RDS to S3 storage, monitoring, backups and CI/CD pipelines, we ensure your applications run reliably and grow without limits.",
      image: "https://readdy.ai/api/search-image?query=Modern%20AWS%20cloud%20infrastructure%20concept%2C%20abstract%20cloud%20computing%20network%20visualization%2C%20orange%20and%20navy%20gradient%20lighting%2C%20premium%20cloud%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-aws-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "EC2", desc: "Scalable compute instances.", icon: "ri-server-line" },
      { title: "RDS", desc: "Managed relational databases.", icon: "ri-database-2-line" },
      { title: "S3", desc: "Durable object storage.", icon: "ri-archive-line" },
      { title: "Security", desc: "IAM, VPC & best practices.", icon: "ri-shield-check-line" },
      { title: "Monitoring", desc: "CloudWatch & alerts.", icon: "ri-line-chart-line" },
      { title: "CI/CD", desc: "Automated deployments.", icon: "ri-git-branch-line" },
    ],
    capabilities: [
      { title: "EC2 & Compute", desc: "Scalable virtual servers.", icon: "ri-server-line" },
      { title: "RDS & Databases", desc: "Managed database services.", icon: "ri-database-2-line" },
      { title: "S3 Storage", desc: "Durable object storage.", icon: "ri-archive-line" },
      { title: "Security", desc: "IAM, VPC and encryption.", icon: "ri-shield-check-line" },
      { title: "Monitoring", desc: "CloudWatch dashboards and alerts.", icon: "ri-line-chart-line" },
      { title: "Backups", desc: "Automated backup & recovery.", icon: "ri-refresh-line" },
      { title: "Scalability", desc: "Auto-scaling and load balancing.", icon: "ri-expand-diagonal-line" },
      { title: "CI/CD", desc: "Automated build and deploy.", icon: "ri-git-branch-line" },
    ],
    stack: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "EC2", icon: "ri-server-line" },
      { name: "RDS", icon: "ri-database-2-line" },
      { name: "S3", icon: "ri-archive-line" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
      { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg" },
    ],
    architecture: [
      { name: "Clients", desc: "Web & mobile via CDN", icon: "ri-window-line" },
      { name: "Load Balancer", desc: "Traffic distribution", icon: "ri-node-tree" },
      { name: "EC2 App Servers", desc: "Laravel / PHP / Node", icon: "ri-server-line" },
      { name: "RDS Database", desc: "MySQL / PostgreSQL", icon: "ri-database-2-line" },
      { name: "Redis", desc: "Cache & queues", icon: "ri-stack-line" },
      { name: "S3", desc: "Files & backups", icon: "ri-archive-line" },
    ],
    benefits: [
      { title: "Reliability", desc: "High availability infrastructure.", icon: "ri-shield-check-line" },
      { title: "Scalability", desc: "Grow resources on demand.", icon: "ri-expand-diagonal-line" },
      { title: "Security", desc: "AWS best practices.", icon: "ri-lock-2-line" },
      { title: "Automation", desc: "CI/CD and monitoring.", icon: "ri-settings-3-line" },
    ],
    relatedServices: [
      { title: "Cloud & DevOps", desc: "AWS deployment & management.", icon: "ri-server-line", href: "/services/cloud-devops" },
      { title: "Laravel Development", desc: "Deploy Laravel on AWS.", icon: "ri-code-s-slash-line", href: "/services/laravel-development" },
      { title: "Security", desc: "Hardened cloud security.", icon: "ri-shield-check-line", href: "/services/cloud-devops" },
    ],
    faq: [
      { q: "Can you migrate our application to AWS?", a: "Yes. We migrate applications and data to AWS with minimal downtime and optimized costs." },
      { q: "Do you manage infrastructure after launch?", a: "Yes. We offer monitoring, backups, security and ongoing infrastructure management." },
    ],
  },
  {
    slug: "ai-development",
    name: "AI Development",
    tagline: "AI Development & Integration",
    hero: {
      badge: "AI Technology",
      headline: "AI Development & Integration",
      subtext:
        "Integrate modern AI capabilities into your applications, SaaS platforms and business workflows.",
      primaryCTA: "Discuss Your AI Project",
      secondaryCTA: "Explore AI Solutions",
    },
    overview: {
      title: "Intelligent capabilities in real products",
      text: "We integrate AI into real products — chatbots, assistants, workflow automation and AI-powered SaaS. Using tools like ChatGPT, Claude and Gemini, we bring practical intelligence to your applications without positioning ourselves as an AI research lab.",
      image: "https://readdy.ai/api/search-image?query=Modern%20AI%20development%20concept%2C%20abstract%20neural%20network%20and%20intelligent%20automation%20visualization%2C%20purple%20and%20pink%20gradient%20lighting%2C%20premium%20AI%20engineering%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1000&height=700&seq=tech-ai-overview&orientation=portrait",
    },
    whyWeUse: [
      { title: "AI API Integration", desc: "Connect AI models to products.", icon: "ri-exchange-line" },
      { title: "ChatGPT Integration", desc: "Add conversational intelligence.", icon: "ri-bubble-chart-line" },
      { title: "Claude Integration", desc: "Sophisticated language models.", icon: "ri-bard-line" },
      { title: "AI Chatbots", desc: "Customer-facing assistants.", icon: "ri-chat-smile-line" },
      { title: "AI Assistants", desc: "Productivity and support tools.", icon: "ri-robot-line" },
      { title: "AI Workflow Automation", desc: "Automate business processes.", icon: "ri-settings-3-line" },
      { title: "AI-powered SaaS", desc: "Intelligent product features.", icon: "ri-cloud-line" },
    ],
    capabilities: [
      { title: "AI API Integration", desc: "Connect AI models to your stack.", icon: "ri-exchange-line" },
      { title: "ChatGPT Integration", desc: "Conversational intelligence.", icon: "ri-bubble-chart-line" },
      { title: "Claude Integration", desc: "Advanced language processing.", icon: "ri-bard-line" },
      { title: "AI Chatbots", desc: "Customer support assistants.", icon: "ri-chat-smile-line" },
      { title: "AI Assistants", desc: "Productivity tools.", icon: "ri-robot-line" },
      { title: "AI Workflow Automation", desc: "Automate repetitive tasks.", icon: "ri-settings-3-line" },
      { title: "AI-powered SaaS", desc: "Intelligent product features.", icon: "ri-cloud-line" },
    ],
    stack: [
      { name: "ChatGPT", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
      { name: "Claude", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/claude-color.svg" },
      { name: "Gemini", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/gemini-color.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
      { name: "Cloud", icon: "ri-cloud-line" },
    ],
    architecture: [
      { name: "Application", desc: "Web / mobile / SaaS", icon: "ri-window-line" },
      { name: "Laravel / API", desc: "Business logic & routing", icon: "ri-code-s-slash-line" },
      { name: "AI Integration Layer", desc: "Prompt & model orchestration", icon: "ri-bard-line" },
      { name: "AI Models", desc: "ChatGPT / Claude / Gemini", icon: "ri-bubble-chart-line" },
      { name: "Database", desc: "User data & context", icon: "ri-database-2-line" },
      { name: "Cloud", desc: "Secure deployment", icon: "ri-cloud-line" },
    ],
    benefits: [
      { title: "Automation", desc: "Reduce manual work.", icon: "ri-settings-3-line" },
      { title: "Better Support", desc: "24/7 AI assistants.", icon: "ri-chat-smile-line" },
      { title: "Product Intelligence", desc: "Smarter features.", icon: "ri-bard-line" },
      { title: "Scalable", desc: "AI that grows with you.", icon: "ri-expand-diagonal-line" },
    ],
    relatedServices: [
      { title: "AI Development", desc: "Integrate AI into products.", icon: "ri-bard-line", href: "/services/ai-development" },
      { title: "AI Solutions", desc: "Detailed AI capabilities.", icon: "ri-lightbulb-line", href: "/ai-solutions" },
      { title: "API Development", desc: "Secure API integration.", icon: "ri-exchange-line", href: "/services/api-development" },
      { title: "SaaS Development", desc: "AI-powered SaaS platforms.", icon: "ri-cloud-line", href: "/services/saas-development" },
    ],
    faq: [
      { q: "What AI capabilities do you integrate?", a: "We integrate chatbots, assistants, content automation, workflow automation and AI features into existing products." },
      { q: "Do you use official AI platforms?", a: "We integrate tools like ChatGPT, Claude and Gemini as part of product builds. We don't claim official partnerships." },
      { q: "Can AI integrate with Laravel?", a: "Yes. Laravel applications can be extended with AI-powered workflows, assistants and automation via APIs." },
    ],
  },
];